/**
 * Polygon Edit Modal
 *
 * Form to edit polygon metadata:
 * - Name
 * - Producer name
 * - Commodity
 * - Country
 * - Region
 */

import { X, Save, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { polygonsApi, getErrorMessage } from '../../lib/api';
import toast from 'react-hot-toast';

interface PolygonEditModalProps {
    polygon: any;
    onClose: () => void;
    onSuccess: () => void;
}

export default function PolygonEditModal({ polygon, onClose, onSuccess }: PolygonEditModalProps) {
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: polygon.name || '',
        producer_name: polygon.producer_name || '',
        legal_name: polygon.legal_name || '',
        organization: polygon.organization || '',
        commodity: polygon.commodity || 'cocoa',
        country: polygon.country || '',
        region: polygon.region || '',
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate required fields
        if (!formData.name.trim()) {
            toast.error('El nombre es requerido');
            return;
        }

        try {
            setSaving(true);

            // Update polygon
            await polygonsApi.update(polygon.id, {
                name: formData.name.trim(),
                producer_name: formData.producer_name.trim() || undefined,
                legal_name: formData.legal_name.trim() || undefined,
                organization: formData.organization.trim() || undefined,
                commodity: formData.commodity,
                country: formData.country.trim() || undefined,
                region: formData.region.trim() || undefined,
            });

            toast.success('Polígono actualizado exitosamente');
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error('Update error:', error);
            const errorMsg = getErrorMessage(error) || 'Error al actualizar polígono';
            toast.error(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    return (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
            <div className="card p-0 w-full max-w-2xl animate-slide-up">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                            Editar Polígono
                        </h2>
                        <p className="text-sm text-gray-500">Actualiza la información del polígono</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        disabled={saving}
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Name */}
                    <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Nombre del Polígono *
                        </label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="input w-full"
                            placeholder="Ej: Finca La Esperanza"
                            disabled={saving}
                        />
                    </div>

                    {/* Producer Name */}
                    <div>
                        <label htmlFor="producer_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Nombre del Productor
                        </label>
                        <input
                            type="text"
                            id="producer_name"
                            name="producer_name"
                            value={formData.producer_name}
                            onChange={handleChange}
                            className="input w-full"
                            placeholder="Ej: Juan García"
                            disabled={saving}
                        />
                    </div>

                    {/* Legal Name */}
                    <div>
                        <label htmlFor="legal_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Nombre Legal
                        </label>
                        <input
                            type="text"
                            id="legal_name"
                            name="legal_name"
                            value={formData.legal_name}
                            onChange={handleChange}
                            className="input w-full"
                            placeholder="Ej: Finca La Esperanza S.A."
                            disabled={saving}
                        />
                    </div>

                    {/* Organization */}
                    <div>
                        <label htmlFor="organization" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Organización
                        </label>
                        <input
                            type="text"
                            id="organization"
                            name="organization"
                            value={formData.organization}
                            onChange={handleChange}
                            className="input w-full"
                            placeholder="Ej: Cooperativa Nacional de Cacao"
                            disabled={saving}
                        />
                    </div>

                    {/* Commodity */}
                    <div>
                        <label htmlFor="commodity" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Commodity
                        </label>
                        <select
                            id="commodity"
                            name="commodity"
                            value={formData.commodity}
                            onChange={handleChange}
                            className="input w-full"
                            disabled={saving}
                        >
                            <option value="cocoa">Cacao</option>
                            <option value="timber">Madera</option>
                            <option value="soy">Soja</option>
                            <option value="palm_oil">Aceite de Palma</option>
                            <option value="coffee">Café</option>
                            <option value="rubber">Caucho</option>
                            <option value="cattle">Ganado</option>
                        </select>
                    </div>

                    {/* Country and Region */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                País
                            </label>
                            <input
                                type="text"
                                id="country"
                                name="country"
                                value={formData.country}
                                onChange={handleChange}
                                className="input w-full"
                                placeholder="Ej: Ecuador"
                                disabled={saving}
                            />
                        </div>

                        <div>
                            <label htmlFor="region" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Región
                            </label>
                            <input
                                type="text"
                                id="region"
                                name="region"
                                value={formData.region}
                                onChange={handleChange}
                                className="input w-full"
                                placeholder="Ej: Esmeraldas"
                                disabled={saving}
                            />
                        </div>
                    </div>

                    {/* Info Note */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                        <p className="text-sm text-blue-900 dark:text-blue-100">
                            <strong>Nota:</strong> La geometría del polígono no puede ser editada. Para cambiar la geometría, elimina este polígono y sube uno nuevo.
                        </p>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn-secondary"
                            disabled={saving}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn-primary flex items-center gap-2"
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    Guardar Cambios
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
